// Globals
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router-dom';

// Store
import { useAppDispatch, useAppState } from '@/store';
import {
  createDeliveryWarehouse,
  deleteDeliveryWarehouse,
  getDeliveryWarehouse,
  getPickupLocations,
  regionsSelector,
  selectQuery,
  updateDeliveryWarehouse,
} from '../../feature';

// Components
import { Modal, ModalSize, showSuccessToast } from '@/components';
import { PlusCircleIcon } from '@/icons';
import { Button, Drawer } from '@/ui';
import { PickupList, PickupPointForm } from '../../components';
import { StyledContentBlock, StyledPageTitle } from '../../components/Styled';
import { StyledActionButtons, StyledCreateButton, StyledTabTitleWrapper } from './PickupContainerStyles';

// Modules
import { Resource, usePermissions } from '@/modules/Permissions';

// Models
import { IPickup } from '@/models/regions.model';
import { ROUTES } from '@/models/router.model';

// Helpers
import { usePagination } from '@/hooks';
import { BasePickupDTO, GetPickupsDTO } from '../../dtos';
import { RegionsAndPickupTabs } from '../../helpers/configs';
import { getInitialPickupValues } from '../../helpers/getInitialPickupValues';
import { TCreatePickingFormValues } from '../../helpers/types';

const PickupContainer = () => {
  const { t } = useTranslation(['regions']);

  const navigate = useNavigate();
  const { id } = useParams<{ tab: string; id?: string }>();
  const [pickup, setPickup] = useState<IPickup | null>(null);
  const [deleteId, setDeleteId] = useState<string | null>(null);
  const { currentPage, onPageChange } = usePagination();

  const dispatch = useAppDispatch();
  const { pickups, isLoading } = useAppState(regionsSelector);
  const query = useAppState(selectQuery);
  const { canManage } = usePermissions();

  const canManagePickup = canManage(Resource.PICKUP);

  const isEditMode = Boolean(id && id !== 'create');

  const getPickupsListHandler = () => {
    const dto = new GetPickupsDTO({ page: currentPage, ...query });
    dispatch(getPickupLocations(dto));
  };

  const onDrawerClose = () => {
    setPickup(null);
    navigate(`${ROUTES.REGIONS_AND_PICKUP_INDEX}/${RegionsAndPickupTabs.PICKUP}`);
  };

  const handleOpenDrawer = async (item?: IPickup) => {
    try {
      if (item?.id) {
        const result = await dispatch(getDeliveryWarehouse(item.id)).unwrap();
        setPickup(result.data);
      }
      return navigate(`${ROUTES.REGIONS_AND_PICKUP_INDEX}/${RegionsAndPickupTabs.PICKUP}/${item?.id ?? 'create'}`);
    } catch (error) {}
  };

  const handleConfirmCreate = async (values: TCreatePickingFormValues) => {
    try {
      const dto = new BasePickupDTO(values);

      const isEdit = !!pickup?.id;

      if (isEdit) {
        await dispatch(
          updateDeliveryWarehouse({
            ...dto,
            id: pickup.id,
          }),
        ).unwrap();
      } else {
        await dispatch(createDeliveryWarehouse(dto)).unwrap();
      }

      showSuccessToast({
        message: t(isEdit ? 'pickup_edit_success_message' : 'pickup_create_success_message'),
      });
      onDrawerClose();
      getPickupsListHandler();
    } catch (error) {}
  };

  const handleConfirmDelete = async () => {
    if (!deleteId) return;
    try {
      await dispatch(deleteDeliveryWarehouse(deleteId)).unwrap();
      setDeleteId(null);
      getPickupsListHandler();
    } catch (error) {}
  };

  const handleDeletePickup = async (item: IPickup) => setDeleteId(item.id);

  const handleCloseDeleteModal = () => setDeleteId(null);

  useEffect(() => {
    getPickupsListHandler();
  }, [currentPage]);

  return (
    <>
      <StyledTabTitleWrapper>
        <StyledPageTitle>{t('pickup_tab_title')}</StyledPageTitle>
        {canManagePickup && (
          <StyledCreateButton onClick={() => handleOpenDrawer()}>
            <PlusCircleIcon />
            {t('pickup_add_point')}
          </StyledCreateButton>
        )}
      </StyledTabTitleWrapper>
      <StyledContentBlock>
        <PickupList
          pickupList={pickups?.data ?? []}
          isLoading={isLoading}
          currentPage={currentPage}
          onPageChange={onPageChange}
          onDelete={handleDeletePickup}
          onUpdate={handleOpenDrawer}
          total={pickups?.meta?.total ?? 0}
        />
        <Drawer
          open={!!id}
          onClose={onDrawerClose}
          width="29rem"
          destroyOnClose
          title={t(isEditMode ? 'pickup_edit_point' : 'pickup_add_point')}
        >
          <PickupPointForm
            onSubmit={handleConfirmCreate}
            isLoading={isLoading}
            initialValues={getInitialPickupValues(pickup)}
            defaultRegion={pickup?.settlement?.region?.name}
            defaultSettlement={pickup?.settlement?.name}
          />
        </Drawer>
        <Modal
          visible={!!deleteId}
          onCancel={handleCloseDeleteModal}
          size={ModalSize.MEDIUM}
          title={t('pickup_delete_modal_title')}
          withFooter
          footer={
            <StyledActionButtons>
              <Button block onClick={handleCloseDeleteModal} type="primary">
                {t('button_common_back', { ns: 'common' })}
              </Button>
              <Button block onClick={handleConfirmDelete} type="text">
                {t('button_common_delete', { ns: 'common' })}
              </Button>
            </StyledActionButtons>
          }
        />
      </StyledContentBlock>
    </>
  );
};

export default PickupContainer;
